import { _imagesURL } from "../constants/API";
import placeholderAvatar from "../assets/images/icon/placeholder_Awatar.png";

/**
 * Standard placeholder avatar used throughout the app when a mentor/user has no photo.
 */
export const DEFAULT_MENTOR_AVATAR = placeholderAvatar;

/**
 * Safely extracts raw image path or URL from any mentor or user object.
 * Checks mentor_profile, doctor_profile, user, and direct fields.
 *
 * @param {Object} mentor
 * @returns {string|null}
 */
export function getMentorRawImage(mentor) {
  if (!mentor || typeof mentor !== "object") return null;

  return (
    mentor?.mentor_profile?.profile_picture ||
    mentor?.mentor_profile?.image ||
    mentor?.mentor_profile?.avatar ||
    mentor?.doctor_profile?.image ||
    mentor?.doctor_profile?.profile_picture ||
    mentor?.user?.mentor_profile?.profile_picture ||
    mentor?.user?.image ||
    mentor?.image ||
    mentor?.profile_picture ||
    mentor?.avatar ||
    mentor?.photo ||
    null
  );
}

/**
 * Resolves a mentor image URL with fallback to placeholderAvatar when null, empty, or invalid.
 *
 * @param {string|Object} rawImg
 * @param {string} fallback
 * @returns {string}
 */
export function resolveMentorImage(rawImg, fallback = placeholderAvatar) {
  if (
    !rawImg ||
    (typeof rawImg !== "string" && typeof rawImg !== "object")
  ) {
    return fallback;
  }

  // Handle case where object is passed
  const target = typeof rawImg === "object" ? getMentorRawImage(rawImg) : rawImg;
  if (!target || typeof target !== "string") {
    return fallback;
  }

  const str = target.trim();
  if (
    !str ||
    ["undefined", "null", "@empty_data_", ""].includes(str.toLowerCase())
  ) {
    return fallback;
  }

  // Handle absolute web URLs, data URIs, blob URIs, and local dev/build asset paths
  if (
    str.startsWith("http://") ||
    str.startsWith("https://") ||
    str.startsWith("data:") ||
    str.startsWith("blob:") ||
    str.startsWith("/src/") ||
    str.startsWith("/assets/") ||
    str.startsWith("/static/") ||
    str.startsWith("/@")
  ) {
    return str;
  }

  const cleanPath = str.startsWith("/") ? str.slice(1) : str;
  return `${_imagesURL}/${cleanPath}`;
}

/**
 * Helper error event handler for <img> tags to safely fallback to placeholderAvatar without infinite loops.
 *
 * @param {React.SyntheticEvent<HTMLImageElement, Event>} e
 * @param {Function} [onLoadedCallback]
 * @param {string} [fallback]
 */
export function handleMentorImageError(e, onLoadedCallback, fallback = placeholderAvatar) {
  if (typeof onLoadedCallback === "function") {
    onLoadedCallback(true);
  }
  if (e?.target && e.target.src !== fallback) {
    e.target.src = fallback;
  }
}
